import beach from '@/assets/images/play/beach.png';
import christmas from '@/assets/images/play/christmas.png';
import city from '@/assets/images/play/city.png';
import forest from '@/assets/images/play/forest.png';
import linkIcon from '@/assets/images/play/link.svg';
import sendIcon from '@/assets/images/play/send.svg';
import space from '@/assets/images/play/space.png';
import tipIcon from '@/assets/images/play/tip.svg';
import xIcon from '@/assets/images/play/x.svg';
import { CountdownTimer } from '@/components/CountdownTimer';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/shadcn/dialog';
import { Skeleton } from '@/components/shadcn/skeleton';
import { isBSCNetwork } from '@/constant';
import { getServerApi } from '@/gql';
import { AdventureType, Lazbubu } from '@/gql/graph/__generated__/request';
import { useAdventurePermit } from '@/hooks/useAdventurePermit';
import { useChats } from '@/hooks/useChats';
import { useDataAnchoringTokenContract } from '@/hooks/useContract';
import useCopyClipboard from '@/hooks/useCopy';
import { useMessageQuota } from '@/hooks/useMessageQuota';
import useSend from '@/hooks/useSend';
import { useSetLevelPermit } from '@/hooks/useSetLevelPermit';
import application from '@/stores/application';
import { getSocialShareLinks } from '@/utils';
import Alert from '@/utils/components/alert';
import { trackAPICall, trackTransaction, trackUserAction } from '@/utils/gtag';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShareIcon from '@mui/icons-material/Share';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { WelcomeGiftModal } from './WelcomeGiftModal';

const Chat = ({ lazbubu }: { lazbubu: Lazbubu }) => {
    const send = useSend();
    const [, setCopied] = useCopyClipboard();
    const dataAnchoringTokenContract = useDataAnchoringTokenContract();

    const {
        chats,
        refetch: refetchChats,
        loading: chatsLoading,
    } = useChats(lazbubu.tokenId);
    const { messageQuota, refetch: refetchMessageQuota } = useMessageQuota(
        lazbubu.tokenId,
    );
    const { getAdventurePermit } = useAdventurePermit(lazbubu.tokenId);
    const { getSetLevelPermit } = useSetLevelPermit(lazbubu.tokenId);
    // const { getCreateMemoryPermit } = useCreateMemoryPermit(lazbubu.tokenId);
    // const { memoryAvailable, getMemoryAvailable } = useMemoryAvailable(
    //     lazbubu.tokenId,
    // );

    const [lastChatId, setLastChatId] = useState(0);
    const [chatList, setChatList] = useState<any[]>([]);
    const [question, setQuestion] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isAdventureLoading, setIsAdventureLoading] = useState(false);
    const [isClaiming, setIsClaiming] = useState(false);
    const [isSettingLevel, setIsSettingLevel] = useState(false);
    const [mode, setMode] = useState<'share' | 'chat'>('chat');
    const [selectedShareIds, setSelectedShareIds] = useState<string[]>([]);
    const [generateChatImageLoading, setGenerateChatImageLoading] =
        useState(false);
    const [shareContent, setShareContent] = useState<{
        imageUrl?: string;
        shareId: string;
    } | null>(null);
    const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
    // const [uploadMemoryLoading, setUploadMemoryLoading] = useState(false);

    // useEffect(() => {
    //     getMemoryAvailable();
    //     const interval = setInterval(() => {
    //         getMemoryAvailable();
    //     }, 5000);
    //     return () => clearInterval(interval);
    // }, []);

    useEffect(() => {
        if (chats && chats.length > 0) {
            if (chats[chats.length - 1].id > lastChatId) {
                refetchMessageQuota();
                setLastChatId(chats[chats.length - 1].id);
                const newChatList = chats.filter(
                    (chat) => chat.id > lastChatId,
                );
                setChatList([...chatList, ...newChatList]);
            }
        }
    }, [chats, lastChatId]);

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo({
                top: document.documentElement.scrollHeight,
                behavior: 'smooth',
            });
        }, 100);
    }, [chatList]);

    useEffect(() => {
        if (mode === 'share') {
            refetchChats();
            setSelectedShareIds([]);
        }
    }, [mode]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            chat(question);
        }
    };

    const chat = async (content: string) => {
        if (content === '' || isLoading || isAdventureLoading || isSettingLevel)
            return;
        if (messageQuota?.availableQuota === 0) {
            Alert({
                type: 'error',
                text: 'No message quota',
            });
            return;
        }
        setQuestion('');
        setIsLoading(true);
        setChatList([
            ...chatList,
            {
                id: new Date().getTime(),
                content,
                role: 'master',
                type: 'normal',
            },
            {
                id: new Date().getTime() + 1,
                content: 'Thinking...',
                role: 'lazbubu',
                type: 'thinking',
            },
        ]);
        try {
            const api = getServerApi();
            const res = await api.chat({
                tokenId: lazbubu.tokenId,
                content,
            });
            if (res.chat) {
                setChatList((prev) => {
                    const newChatList = [...prev];
                    newChatList[newChatList.length - 2].id = res.chat.id - 1;
                    newChatList[newChatList.length - 1] = res.chat;
                    return newChatList;
                });
                setLastChatId(res.chat.id);
            }
        } catch (error) {
            console.error(error);
            setChatList((prev) => {
                const newChatList = [...prev];
                newChatList[newChatList.length - 1].content =
                    'Oops...Something wrong';
                newChatList[newChatList.length - 1].type = 'error';
                return newChatList;
            });
        } finally {
            setIsLoading(false);
            refetchMessageQuota();
        }
    };

    const deleteLastThinking = () => {
        setChatList((prev) => {
            const newChatList = [...prev];
            if (newChatList[newChatList.length - 1].type === 'thinking') {
                newChatList.pop();
            }
            return newChatList;
        });
    };

    const handleAdventure = async (adventureType: AdventureType) => {
        if (isAdventureLoading || isSettingLevel || isLoading) return;
        setChatList([
            ...chatList,
            {
                id: new Date().getTime(),
                content: 'Starting Adventure...',
                role: 'master',
                type: 'thinking',
            },
        ]);
        setIsAdventureLoading(true);
        try {
            const permit = await getAdventurePermit(adventureType);
            if (permit?.data && permit?.permit && dataAnchoringTokenContract) {
                const permitData = JSON.parse(permit.data);
                const permitValue = JSON.parse(permit.permit);
                await send({
                    contract: dataAnchoringTokenContract,
                    method: 'adventure',
                    params: [
                        lazbubu.tokenId,
                        Number(permitData.adventureType),
                        permitData.contentHash,
                        [
                            Number(permitValue.permitType),
                            BigInt(permitValue.nonce),
                            permitValue.dataHash,
                            BigInt(permitValue.expire),
                            permitValue.sig,
                        ],
                    ],
                    onSuccess: () => {
                        let isFetching = false;
                        let iterationCount = 0;
                        const interval = setInterval(async () => {
                            if (!isFetching) {
                                try {
                                    isFetching = true;
                                    if (iterationCount >= 5) {
                                        deleteLastThinking();
                                        setIsAdventureLoading(false);
                                        clearInterval(interval);
                                        return;
                                    }

                                    const newChats = await refetchChats();
                                    if (
                                        newChats[newChats.length - 1].id >
                                        lastChatId
                                    ) {
                                        deleteLastThinking();
                                        setIsAdventureLoading(false);
                                        clearInterval(interval);
                                    }
                                } catch (error) {
                                    console.error(
                                        'Update chats failed:',
                                        error,
                                    );
                                } finally {
                                    isFetching = false;
                                    iterationCount++;
                                }
                            }
                        }, 1000);
                    },
                    onError: (e) => {
                        if (e?.code !== 'ACTION_REJECTED') {
                            Alert({
                                type: 'error',
                                text:
                                    e?.shortMessage ||
                                    e?.message ||
                                    'Adventure failed. Please try again.',
                            });
                        }
                        deleteLastThinking();
                        setIsAdventureLoading(false);
                    },
                });
            } else {
                Alert({
                    type: 'error',
                    text: 'No permit to start adventure. Please try again.',
                });
                deleteLastThinking();
                setIsAdventureLoading(false);
            }
        } catch (error) {
            console.error('Adventure failed:', error);
            const e = error as any;
            if (e?.code !== 'ACTION_REJECTED') {
                Alert({
                    type: 'error',
                    text:
                        e?.shortMessage ||
                        e?.message ||
                        'Adventure failed. Please try again.',
                });
            }
            deleteLastThinking();
            setIsAdventureLoading(false);
        }
    };

    const claimMessage = async (type?: 'gift') => {
        if (isClaiming) return;
        trackUserAction(
            type === 'gift' ? 'claim_gift_message' : 'claim_message_quota',
            'user_interaction',
            {
                tokenId: lazbubu.tokenId,
            },
        );
        setIsClaiming(true);
        try {
            trackTransaction(
                type === 'gift' ? 'claim_gift_message' : 'claim_message_quota',
                'initiated',
                {
                    tokenId: lazbubu.tokenId,
                },
            );
            await send({
                contract: dataAnchoringTokenContract,
                method: 'claimMessageQuota',
                params: [lazbubu.tokenId],
                onSuccess: async () => {
                    trackTransaction(
                        type === 'gift'
                            ? 'claim_gift_message'
                            : 'claim_message_quota',
                        'success',
                        {
                            tokenId: lazbubu.tokenId,
                        },
                    );
                    let isFetching = false;
                    let iterationCount = 0;
                    const interval = setInterval(async () => {
                        if (!isFetching) {
                            try {
                                isFetching = true;
                                if (iterationCount >= 5) {
                                    clearInterval(interval);
                                    setIsClaiming(false);
                                    return;
                                }
                                const newMessageQuota =
                                    await refetchMessageQuota();
                                if (newMessageQuota?.claimed) {
                                    clearInterval(interval);
                                    setIsClaiming(false);
                                }
                            } catch (error) {
                                console.error('Claim failed:', error);
                            } finally {
                                isFetching = false;
                                iterationCount++;
                            }
                        }
                    }, 1000);
                },
                onError: (e) => {
                    trackTransaction(
                        type === 'gift'
                            ? 'claim_gift_message'
                            : 'claim_message_quota',
                        'failed',
                        {
                            tokenId: lazbubu.tokenId,
                        },
                    );
                    if (e?.code !== 'ACTION_REJECTED') {
                        Alert({
                            type: 'error',
                            text:
                                e?.shortMessage ||
                                e?.message ||
                                'Claim failed. Please try again.',
                        });
                    }
                    setIsClaiming(false);
                },
            });
        } catch (error) {
            trackTransaction(
                type === 'gift' ? 'claim_gift_message' : 'claim_message_quota',
                'failed',
                {
                    tokenId: lazbubu.tokenId,
                },
            );
            console.error('Claim failed:', error);
            const e = error as any;
            if (e?.code !== 'ACTION_REJECTED') {
                Alert({
                    type: 'error',
                    text:
                        e?.shortMessage ||
                        e?.message ||
                        'Claim failed. Please try again.',
                });
            }
            setIsClaiming(false);
        }
    };

    const setLevel = async () => {
        if (isSettingLevel || isLoading || isAdventureLoading) return;
        setChatList([
            ...chatList,
            {
                id: new Date().getTime(),
                content: 'Updating...',
                role: 'master',
                type: 'thinking',
            },
        ]);
        setIsSettingLevel(true);
        try {
            const permit = await getSetLevelPermit();
            if (permit?.data && permit?.permit && dataAnchoringTokenContract) {
                const permitData = JSON.parse(permit.data);
                const permitValue = JSON.parse(permit.permit);
                await send({
                    contract: dataAnchoringTokenContract,
                    method: 'setLevel',
                    params: [
                        BigInt(lazbubu.tokenId),
                        Number(permitData.level),
                        Boolean(permitData.mature),
                        [
                            Number(permitValue.permitType),
                            BigInt(permitValue.nonce),
                            permitValue.dataHash,
                            BigInt(permitValue.expire),
                            permitValue.sig,
                        ],
                    ],
                    onSuccess: () => {
                        const api = getServerApi();
                        api.syncLevel({
                            tokenId: lazbubu.tokenId,
                        });
                        let isFetching = false;
                        let iterationCount = 0;
                        const interval = setInterval(async () => {
                            if (!isFetching) {
                                try {
                                    isFetching = true;
                                    if (iterationCount >= 5) {
                                        deleteLastThinking();
                                        setIsSettingLevel(false);
                                        clearInterval(interval);
                                        return;
                                    }
                                    const newChats = await refetchChats();
                                    if (
                                        newChats[newChats.length - 1].id >
                                        lastChatId
                                    ) {
                                        deleteLastThinking();
                                        setIsSettingLevel(false);
                                        clearInterval(interval);
                                    }
                                } catch (error) {
                                    console.error(
                                        'Update chats failed:',
                                        error,
                                    );
                                } finally {
                                    isFetching = false;
                                    iterationCount++;
                                }
                            }
                        }, 1000);
                    },
                    onError: (e) => {
                        if (e?.code !== 'ACTION_REJECTED') {
                            Alert({
                                type: 'error',
                                text:
                                    e?.shortMessage ||
                                    e?.message ||
                                    'Set level failed. Please try again.',
                            });
                        }
                        deleteLastThinking();
                        setIsSettingLevel(false);
                    },
                });
            } else {
                let isFetching = false;
                let iterationCount = 0;
                const interval = setInterval(async () => {
                    if (!isFetching) {
                        try {
                            isFetching = true;
                            if (iterationCount >= 5) {
                                deleteLastThinking();
                                setIsSettingLevel(false);
                                clearInterval(interval);
                                return;
                            }
                            const newChats = await refetchChats();
                            if (newChats[newChats.length - 1].id > lastChatId) {
                                deleteLastThinking();
                                setIsSettingLevel(false);
                                clearInterval(interval);
                            }
                        } catch (error) {
                            console.error('Update chats failed:', error);
                        } finally {
                            isFetching = false;
                            iterationCount++;
                        }
                    }
                }, 1000);
            }
        } catch (error) {
            console.error('Set level failed:', error);
            const e = error as any;
            if (e?.code !== 'ACTION_REJECTED') {
                Alert({
                    type: 'error',
                    text:
                        e?.shortMessage ||
                        e?.message ||
                        'Set level failed. Please try again.',
                });
            }
            deleteLastThinking();
            setIsSettingLevel(false);
        }
    };

    const selectShareId = (id: string) => {
        if (selectedShareIds.includes(id)) {
            setSelectedShareIds(selectedShareIds.filter((i) => i !== id));
        } else {
            setSelectedShareIds([...selectedShareIds, id]);
        }
    };

    const generateChatImage = async () => {
        if (generateChatImageLoading || selectedShareIds.length === 0) return;
        try {
            setShareContent(null);
            setGenerateChatImageLoading(true);
            trackAPICall('generate_chat_image', 'start');
            const api = getServerApi();
            const res = await api.generateChatImage({
                chatIds: selectedShareIds.sort((a, b) => Number(a) - Number(b)),
            });
            if (res.generateChatImage?.shareId) {
                trackAPICall('generate_chat_image', 'success');
                setShareContent(res.generateChatImage);
                return res.generateChatImage?.shareId;
            }
            trackAPICall('generate_chat_image', 'error', undefined, {
                error_message: 'Generate image failed. Please try again.',
            });
            setIsShareDialogOpen(false);
            Alert({
                type: 'error',
                text: 'Generate image failed. Please try again.',
            });
        } catch (error) {
            trackAPICall('generate_chat_image', 'error', undefined, {
                error_message:
                    error instanceof Error ? error.message : String(error),
            });
            setIsShareDialogOpen(false);
            Alert({
                type: 'error',
                text: 'Generate image failed. Please try again.',
            });
            console.error('Generate image failed:', error);
        } finally {
            setGenerateChatImageLoading(false);
        }
    };

    const copyShareLink = async () => {
        if (shareContent) {
            trackUserAction('click_copy_share_link_btn', 'user_interaction', {
                tokenId: lazbubu.tokenId,
            });
            setCopied(
                isBSCNetwork
                    ? `https://proxy.lazbubu.ai/chatShareBsc/${shareContent.shareId}`
                    : `https://agents.lazpad.fun/chatShare/${shareContent.shareId}`,
            );
            Alert({
                type: 'success',
                text: 'Copied!',
            });
        }
    };

    const shareOnX = async () => {
        if (shareContent) {
            trackUserAction('click_share_on_x_btn', 'user_interaction', {
                tokenId: lazbubu.tokenId,
            });
            const shareLinks = getSocialShareLinks(
                isBSCNetwork
                    ? `https://proxy.lazbubu.ai/chatShareBsc/${shareContent.shareId}`
                    : `https://agents.lazpad.fun/chatShare/${shareContent.shareId}`,
                'Have a great conversation with my Lazbubu @LazbubuAI',
            );
            window.open(shareLinks.twitter, '_blank');
        }
    };

    // const uploadMemory = async () => {
    //     if (
    //         memoryAvailable &&
    //         dataAnchoringTokenContract &&
    //         !uploadMemoryLoading
    //     ) {
    //         setUploadMemoryLoading(true);
    //         const permit = await getCreateMemoryPermit();
    //         if (!permit?.data || !permit?.permit) {
    //             Alert({
    //                 type: 'error',
    //                 text: 'get permit failed. Please try again.',
    //             });
    //             return;
    //         }
    //         const permitData = JSON.parse(permit.data);
    //         const permitValue = JSON.parse(permit.permit);
    //         send({
    //             contract: dataAnchoringTokenContract,
    //             method: 'createMemory',
    //             params: [
    //                 lazbubu.tokenId,
    //                 permitData.contentHash,
    //                 [
    //                     Number(permitValue.permitType),
    //                     BigInt(permitValue.nonce),
    //                     permitValue.dataHash,
    //                     BigInt(permitValue.expire),
    //                     permitValue.sig,
    //                 ],
    //             ],
    //             onSuccess: () => {
    //                 getMemoryAvailable();
    //                 setUploadMemoryLoading(false);
    //             },
    //             onError: (e) => {
    //                 setUploadMemoryLoading(false);
    //                 if (e?.code !== 'ACTION_REJECTED') {
    //                     Alert({
    //                         type: 'error',
    //                         text:
    //                             e?.shortMessage ||
    //                             e?.message ||
    //                             'Memory upload failed. Please try again.',
    //                     });
    //                 }
    //             },
    //         });
    //     }
    // };

    const ChatItem = ({
        item,
        mode,
    }: {
        item: any;
        mode: 'share' | 'chat';
    }) => {
        return (
            <div className="flex flex-col gap-[20px] max-md:gap-[15px]">
                <div className="flex items-center gap-[20px] max-md:gap-[15px]">
                    {mode === 'share' && (
                        <Checkbox
                            checked={selectedShareIds.includes(String(item.id))}
                            onChange={() => selectShareId(String(item.id))}
                        />
                    )}
                    <div
                        className={`px-[20px] max-md:px-[15px] flex items-center leading-[20px] gap-[10px] py-[10px] max-md:py-[8px] text-[12px] max-md:text-[11px] rounded-[16px] border ${
                            item.role === 'master'
                                ? 'bg-black text-white border-black ml-auto'
                                : 'bg-white border-primary/10'
                        } ${
                            item.type === 'error'
                                ? 'border-error text-error bg-error/10'
                                : ''
                        }`}
                    >
                        {item.type === 'thinking' && (
                            <RefreshIcon className="w-[16px] max-md:w-[14px] animate-spin" />
                        )}
                        <div>
                            <ReactMarkdown>{item.content}</ReactMarkdown>
                        </div>
                    </div>
                    {item.type === 'adventure_reward' && !!item.score && (
                        <div className="text-primary font-semibold max-md:text-[11px]">
                            +{item.score}
                        </div>
                    )}
                </div>
                {item.type === 'welcome' && !messageQuota?.claimed && (
                    <WelcomeGiftModal
                        bubuName={lazbubu?.name || ''}
                        claimMessage={claimMessage}
                        isClaiming={isClaiming}
                        className={`ml-[${mode === 'share' ? '62px' : '0px'}]`}
                    />
                )}
                {item.type === 'update_dat' && (
                    <button
                        className={`bg-border text-[12px] self-start max-md:text-[11px] rounded-[16px] px-[20px] max-md:px-[15px] py-[10px] max-md:py-[8px] ${
                            mode === 'share' ? 'ml-[62px]' : ''
                        }`}
                        onClick={setLevel}
                        disabled={isSettingLevel}
                    >
                        Update DAT
                    </button>
                )}
                {item.type === 'adventure_prompt' && (
                    <div
                        className={`flex gap-[20px] max-md:gap-[10px] max-sm:flex-col text-[12px] max-md:text-[11px] ${
                            mode === 'share' ? 'ml-[62px]' : ''
                        }`}
                    >
                        <button
                            className="flex gap-[10px] max-md:gap-[8px] bg-border items-center rounded-[16px] px-[20px] max-md:px-[15px] py-[10px] max-md:py-[8px]"
                            onClick={() =>
                                handleAdventure(AdventureType.Forest)
                            }
                            disabled={isAdventureLoading}
                        >
                            <img
                                src={forest}
                                className="w-[24px] h-[24px] max-md:w-[20px] max-md:h-[20px]"
                            />
                            <div>Go to the Forest</div>
                        </button>
                        <button
                            className="flex gap-[10px] max-md:gap-[8px] bg-border items-center rounded-[16px] px-[20px] max-md:px-[15px] py-[10px] max-md:py-[8px]"
                            onClick={() => handleAdventure(AdventureType.City)}
                            disabled={isAdventureLoading}
                        >
                            <img
                                src={city}
                                className="w-[24px] h-[24px] max-md:w-[20px] max-md:h-[20px]"
                            />
                            <div>Explore the City</div>
                        </button>
                        <button
                            className="flex gap-[10px] max-md:gap-[8px] bg-border items-center rounded-[16px] px-[20px] max-md:px-[15px] py-[10px] max-md:py-[8px]"
                            onClick={() => handleAdventure(AdventureType.Beach)}
                            disabled={isAdventureLoading}
                        >
                            <img
                                src={beach}
                                className="w-[24px] h-[24px] max-md:w-[20px] max-md:h-[20px]"
                            />
                            <div>Relax at the Beach</div>
                        </button>
                        <button
                            className="flex gap-[10px] max-md:gap-[8px] bg-border items-center rounded-[16px] px-[20px] max-md:px-[15px] py-[10px] max-md:py-[8px]"
                            onClick={() => handleAdventure(AdventureType.Space)}
                            disabled={isAdventureLoading}
                        >
                            <img
                                src={space}
                                className="w-[24px] h-[24px] max-md:w-[20px] max-md:h-[20px]"
                            />
                            <div>Float Around in Space</div>
                        </button>
                        {/* <button
                            className="flex gap-[10px] max-md:gap-[8px] bg-border items-center rounded-[16px] px-[20px] max-md:px-[15px] py-[10px] max-md:py-[8px]"
                            onClick={() =>
                                handleAdventure(AdventureType.Holloween)
                            }
                            disabled={isAdventureLoading}
                        >
                            <img
                                src={halloween}
                                className="w-[24px] h-[24px] max-md:w-[20px] max-md:h-[20px]"
                            />
                            <div>Celebrate Halloween</div>
                        </button> */}
                        <button
                            className="flex gap-[10px] max-md:gap-[8px] bg-border items-center rounded-[16px] px-[20px] max-md:px-[15px] py-[10px] max-md:py-[8px]"
                            onClick={() =>
                                handleAdventure(AdventureType.Christmas)
                            }
                            disabled={isAdventureLoading}
                        >
                            <img
                                src={christmas}
                                className="w-[24px] h-[24px] max-md:w-[20px] max-md:h-[20px]"
                            />
                            <div>Winter Quest</div>
                        </button>
                    </div>
                )}
                {item.type === 'adventure_reward' && !!item.image && (
                    <div
                        className={`flex gap-[20px] text-[12px] max-md:gap-[15px] ${
                            mode === 'share' ? 'ml-[62px]' : ''
                        }`}
                    >
                        <img
                            src={item.image}
                            className="w-[200px] h-[200px] rounded-[32px] max-md:w-[150px] max-md:h-[150px] max-sm:rounded-[24px]"
                        />
                    </div>
                )}
            </div>
        );
    };

    const ChatSkeleton = () => {
        return (
            <div className="flex flex-col gap-[20px] max-md:gap-[15px]">
                <div className="flex items-center gap-[20px] max-md:gap-[15px] justify-end">
                    <div className="px-[20px] max-md:px-[15px] flex items-center leading-[20px] gap-[10px] py-[10px] max-md:py-[8px] rounded-[16px] border bg-black border-black">
                        <Skeleton className="w-[150px] h-[20px] max-md:w-[100px] max-md:h-[16px] bg-white/20" />
                    </div>
                </div>
                <div className="flex items-center gap-[20px] max-md:gap-[15px]">
                    <div className="px-[20px] max-md:px-[15px] flex items-center leading-[20px] gap-[10px] py-[10px] max-md:py-[8px] rounded-[16px] border bg-white border-primary/10">
                        <Skeleton className="w-[200px] h-[20px] max-md:w-[150px] max-md:h-[16px]" />
                    </div>
                </div>

                <div className="flex items-center gap-[20px] max-md:gap-[15px] justify-end">
                    <div className="px-[20px] max-md:px-[15px] flex items-center leading-[20px] gap-[10px] py-[10px] max-md:py-[8px] rounded-[16px] border bg-black border-black">
                        <Skeleton className="w-[150px] h-[20px] max-md:w-[100px] max-md:h-[16px] bg-white/20" />
                    </div>
                </div>
                <div className="flex items-center gap-[20px] max-md:gap-[15px]">
                    <div className="px-[20px] max-md:px-[15px] flex items-center leading-[20px] gap-[10px] py-[10px] max-md:py-[8px] rounded-[16px] border bg-white border-primary/10">
                        <Skeleton className="w-[300px] h-[20px] max-md:w-[200px] max-md:h-[16px]" />
                    </div>
                </div>

                <div className="flex items-center gap-[20px] max-md:gap-[15px] justify-end">
                    <div className="px-[20px] max-md:px-[15px] flex items-center leading-[20px] gap-[10px] py-[10px] max-md:py-[8px] rounded-[16px] border bg-black border-black">
                        <Skeleton className="w-[150px] h-[20px] max-md:w-[100px] max-md:h-[16px] bg-white/20" />
                    </div>
                </div>
                <div className="flex items-center gap-[20px] max-md:gap-[15px]">
                    <div className="px-[20px] max-md:px-[15px] flex items-center leading-[20px] gap-[10px] py-[10px] max-md:py-[8px] rounded-[16px] border bg-white border-primary/10">
                        <Skeleton className="w-[300px] h-[20px] max-md:w-[200px] max-md:h-[16px]" />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {mode === 'share' && (
                <button
                    onClick={() => setMode('chat')}
                    className={`items-center border border-primary/10 rounded-[12px] px-[10px] py-[8px] z-10 bg-white fixed flex top-[100px] max-md:top-[80px] ${
                        !application.isExploreNavDrawerOpen
                            ? 'left-[190px] max-md:left-[110px]'
                            : 'left-[370px] max-md:left-[110px]'
                    }`}
                >
                    <span>Share Off</span>
                </button>
            )}
            <div className="absolute top-0 right-[100px] px-[10px] py-[11px] max-md:rounded-[8px] gap-[10px] max-md:gap-[5px] flex items-center justify-center max-md:right-[20px] max-md:px-[6px] max-md:py-[7px] max-md:text-[10px] bg-white rounded-[12px] text-[12px]">
                <img
                    src={tipIcon}
                    className="w-[16px] h-[16px] max-md:w-[10px] max-md:h-[10px]"
                />
                Show up to 20 messages
            </div>
            <div className="flex flex-col gap-[20px] max-md:gap-[15px] w-full pb-[180px] max-md:pb-[200px] mt-[20px]">
                {chatsLoading && chatList.length === 0 ? (
                    <ChatSkeleton />
                ) : (
                    chatList.map((item) => (
                        <ChatItem key={item.id} item={item} mode={mode} />
                    ))
                )}
                {mode === 'chat' ? (
                    <div
                        className={`px-[40px] max-md:px-[20px] p-[20px] max-md:p-[15px] fixed left-0 right-0 bottom-0 transition-all bg-background ${
                            application.isExploreNavDrawerOpen
                                ? 'md:pl-[280px]'
                                : 'md:pl-[40px]'
                        } ${
                            application.isLazbubuDrawerOpen
                                ? 'md:pr-[390px]'
                                : 'md:pr-[40px]'
                        }`}
                    >
                        <div className="flex items-center mb-[20px] gap-[10px] max-md:gap-[8px] max-sm:gap-[5px] max-md:mb-[15px]">
                            <div className="flex gap-[10px] overflow-x-auto max-md:gap-[8px] max-sm:gap-[5px]">
                                <div
                                    className="text-[12px] shrink-0 max-md:text-[11px] max-sm:text-[10px] bg-white text-primary border cursor-pointer border-primary rounded-[16px] px-[10px] max-md:px-[8px] py-[10px] max-md:py-[8px] max-sm:px-[6px] max-sm:py-[6px]"
                                    onClick={() => chat('Start Adventure')}
                                >
                                    Start Adventure
                                </div>
                                <div
                                    className="text-[12px] shrink-0 max-md:text-[11px] max-sm:text-[10px] bg-white text-primary border cursor-pointer border-primary rounded-[16px] px-[10px] max-md:px-[8px] py-[10px] max-md:py-[8px] max-sm:px-[6px] max-sm:py-[6px]"
                                    onClick={() => chat('When to mature?')}
                                >
                                    When to mature?
                                </div>
                                <div
                                    className="text-[12px] shrink-0 max-md:text-[11px] max-sm:text-[10px] bg-white text-primary border cursor-pointer border-primary rounded-[16px] px-[10px] max-md:px-[8px] py-[10px] max-md:py-[8px] max-sm:px-[6px] max-sm:py-[6px]"
                                    onClick={() => chat("What's the reward?")}
                                >
                                    What's the reward?
                                </div>
                                {/* <div
                                    className="text-[12px] shrink-0 max-md:text-[11px] max-sm:text-[10px] bg-white text-primary border cursor-pointer border-primary rounded-[16px] px-[10px] max-md:px-[8px] py-[10px] max-md:py-[8px] max-sm:px-[6px] max-sm:py-[6px]"
                                    onClick={() => chat('Claim test token')}
                                >
                                    Claim test token
                                </div> */}
                            </div>
                            {/* <TooltipProvider
                                delayDuration={!memoryAvailable ? 0 : 3000}
                            >
                                <Tooltip>
                                    <TooltipTrigger asChild className="ml-auto">
                                        <button
                                            className="text-[12px] shrink-0 max-md:text-[11px] disabled:bg-white max-sm:text-[10px] disabled:border-gray disabled:text-gray bg-primary text-white border border-primary rounded-[16px] px-[10px] max-md:px-[8px] py-[10px] max-md:py-[8px] max-sm:px-[6px] max-sm:py-[6px]"
                                            onClick={uploadMemory}
                                            disabled={
                                                !memoryAvailable ||
                                                uploadMemoryLoading
                                            }
                                        >
                                            Upload memory
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>
                                            Chat more with Lazbubu to create
                                            special memories together
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider> */}
                        </div>
                        <div className="flex gap-[20px] flex-wrap max-md:gap-[15px] max-sm:gap-[10px] h-[52px] max-md:h-[70px]">
                            {!messageQuota?.claimed ? (
                                <button
                                    className="text-[12px] max-md:justify-center max-md:w-full max-md:text-[11px] max-sm:text-[10px] bg-black text-white px-[20px] max-md:px-[15px] max-sm:px-[12px] h-full max-md:h-[30px] flex items-center rounded-[16px] cursor-pointer"
                                    onClick={() => claimMessage()}
                                    disabled={isClaiming}
                                >
                                    Claim today's message
                                </button>
                            ) : (
                                <div className="w-[140px] max-md:w-full text-[12px] max-md:text-[11px] max-sm:text-[10px] bg-white border-border px-[20px] max-md:px-[15px] max-md:h-[30px] max-sm:px-[12px] h-full flex items-center rounded-[16px]">
                                    <span>
                                        {messageQuota?.availableQuota}/
                                        {messageQuota?.totalMessageQuota}
                                    </span>
                                    <span className="text-primary ml-auto font-semibold">
                                        <CountdownTimer
                                            resetTime={
                                                messageQuota?.resetTime || ''
                                            }
                                        />
                                    </span>
                                </div>
                            )}
                            <div
                                className={`flex-1 flex gap-[10px] max-md:gap-[8px] items-center border px-[20px] max-md:px-[15px] max-sm:px-[12px] h-full max-md:h-[30px] bg-white border-border rounded-[16px] ${
                                    (messageQuota?.availableQuota ?? 0) <= 0
                                        ? 'opacity-50'
                                        : ''
                                }`}
                            >
                                <input
                                    type="text"
                                    placeholder={`${
                                        (messageQuota?.availableQuota ?? 0) <= 0
                                            ? 'No message quota'
                                            : `Talk to ${lazbubu?.name}...`
                                    }`}
                                    className="flex-1 text-[12px] max-md:text-[11px] max-sm:text-[10px] disabled:bg-transparent"
                                    value={question}
                                    onChange={(e) =>
                                        setQuestion(e.target.value)
                                    }
                                    onKeyDown={handleKeyDown}
                                    disabled={
                                        (messageQuota?.availableQuota ?? 0) <= 0
                                    }
                                />
                                <button
                                    className={`bg-black h-[32px] w-[32px] max-md:h-[20px] max-md:w-[20px] text-white flex items-center justify-center rounded-[10px] max-md:rounded-[5px]`}
                                    onClick={() => chat(question)}
                                    disabled={
                                        (messageQuota?.availableQuota ?? 0) <= 0
                                    }
                                >
                                    <img
                                        src={sendIcon}
                                        className="w-[16px] h-[16px] max-md:w-[10px] max-md:h-[10px]"
                                    />
                                </button>
                            </div>
                            <button
                                className="bg-white border-border rounded-[16px] flex items-center h-full max-md:h-[30px] gap-[8px] px-[18px] text-[12px] max-md:text-[11px] max-sm:text-[10px]"
                                onClick={() => {
                                    setMode('share');
                                    trackUserAction(
                                        'click_share_chat_btn',
                                        'user_interaction',
                                        {
                                            tokenId: lazbubu.tokenId,
                                        },
                                    );
                                }}
                            >
                                <ShareIcon className="w-[20px] text-primary max-md:w-[15px]" />
                                <span className="max-md:hidden">share</span>
                            </button>
                        </div>
                    </div>
                ) : (
                    <div
                        className={`px-[120px] max-md:px-[20px] py-[20px] fixed left-0 right-0 bottom-0 transition-all bg-white flex items-center gap-[20px] max-md:gap-[10px] ${
                            application.isExploreNavDrawerOpen
                                ? 'md:pl-[280px]'
                                : 'md:pl-[40px]'
                        } ${
                            application.isLazbubuDrawerOpen
                                ? 'md:pr-[390px]'
                                : 'md:pr-[40px]'
                        }`}
                    >
                        <div className="text-[14px] max-md:text-[12px]">
                            {selectedShareIds.length} items selected
                        </div>
                        <Dialog
                            open={isShareDialogOpen}
                            onOpenChange={setIsShareDialogOpen}
                        >
                            <DialogTrigger asChild>
                                <button
                                    className="bg-white text-[12px] ml-auto border-border disabled:opacity-50 disabled:cursor-not-allowed border px-[20px] py-[10px] rounded-[16px] gap-[10px] max-md:px-[10px] max-md:py-[5px] max-md:text-[10px]"
                                    onClick={generateChatImage}
                                    disabled={
                                        selectedShareIds.length === 0 ||
                                        generateChatImageLoading
                                    }
                                >
                                    <span>Share</span>
                                </button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-md">
                                <DialogHeader>
                                    <DialogTitle>Share</DialogTitle>
                                </DialogHeader>
                                <div className="flex justify-center items-center min-h-[100px]">
                                    {shareContent?.imageUrl ? (
                                        <img
                                            src={shareContent?.imageUrl}
                                            className="w-[600px] h-[300px] rounded-[32px] max-md:w-full max-md:h-auto max-sm:rounded-[24px]"
                                        />
                                    ) : (
                                        <CircularProgress
                                            size={32}
                                            className="text-black"
                                            thickness={7}
                                        />
                                    )}
                                </div>
                                {shareContent && (
                                    <DialogFooter className="flex items-center flex-row gap-[10px]">
                                        <button
                                            className="bg-white text-[12px] ml-auto border-border border px-[20px] py-[10px] flex items-center rounded-[16px] gap-[10px] max-md:px-[10px] max-md:py-[5px] max-md:text-[10px]"
                                            onClick={copyShareLink}
                                        >
                                            <img
                                                src={linkIcon}
                                                className="w-[20px] h-[20px] max-md:w-[15px] max-md:h-[15px]"
                                            />
                                            <span>Copy link</span>
                                        </button>
                                        <button
                                            className="bg-white text-[12px] border-border border px-[20px] py-[10px] flex items-center rounded-[16px] gap-[10px] max-md:px-[10px] max-md:py-[5px] max-md:text-[10px]"
                                            onClick={shareOnX}
                                        >
                                            <img
                                                src={xIcon}
                                                className="w-[20px] h-[20px] max-md:w-[15px] max-md:h-[15px]"
                                            />
                                            <span>Share on X</span>
                                        </button>
                                    </DialogFooter>
                                )}
                            </DialogContent>
                        </Dialog>
                    </div>
                )}
            </div>
        </>
    );
};

export default observer(Chat);
